// ============================================
// Dose Log Modal - Quick-log bottom sheet
// Supports single protocol (pre-selected) or
// multi-protocol picker when opened from nav bar
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Input, DosingDisclaimer } from '../../components/Components';
import { DrawChangeModal } from '../../components/DrawChangeModal';
import { InjectionSite, INJECTION_SITE_LABELS } from '../../data/types';
import type { Protocol, DoseLog, InventoryItem, ScheduledDose } from '../../data/types';
import { logDoseWithInventory, logGroupDoseWithInventory, archiveVialAndUnlink, fetchLastDoseForPeptide, maybeAutoSwitchEmptyVial } from '../../data/cloud';
import { resolveVialForComponent } from '../../data/vialResolution';
import { buildAdministrationLogs } from '../../data/groupDosing';
import { formatUnits, formatNumber, unitsForDose, groupComponents, type ComponentGroup } from '../../utils/calculations';
import { isOffProtocolUnits, isValidDrawUnits } from './doseLogUnits';
import { getCurrentPhase } from '../../utils/protocol';
import { useToast } from '../../components/Toast';
import { hapticNotification } from '../../utils/native';
import { uuid } from '../../utils/uuid';
import './DoseLogModal.css';

// Selection model (v3): the pickable unit is the ADMINISTRATION - a
// combined group is ONE entry (its lead component anchors phase and
// vial lookups); a single is its own entry. The index selects into
// groupComponents(protocol), not protocol.components.
function getGroupAt(protocol: Protocol, idx: number): ComponentGroup | undefined {
  const groups = groupComponents(protocol);
  if (groups.length === 0) return undefined;
  return groups[idx] ?? groups[0];
}
function getComponentAt(protocol: Protocol, idx: number) {
  return getGroupAt(protocol, idx)?.components[0];
}
function getPhaseAt(protocol: Protocol, idx: number) {
  const c = getComponentAt(protocol, idx);
  if (!c) return undefined;
  return getCurrentPhase(c);
}
/** Combined groups: TOTAL mcg in one draw. Singles: the phase dose. */
function getDoseMcgAt(protocol: Protocol, idx: number): number {
  const g = getGroupAt(protocol, idx);
  if (!g) return 0;
  if (g.isCombined) {
    return g.components.reduce((sum, c) => sum + getCurrentPhase(c).doseMcg, 0);
  }
  return getPhaseAt(protocol, idx)?.doseMcg ?? 0;
}
/** Combined groups: the shared drawUnits. Singles: dose / concentration. */
function getUnitsAt(protocol: Protocol, idx: number): number {
  const g = getGroupAt(protocol, idx);
  if (!g) return 0;
  const lead = g.components[0];
  if (g.isCombined) {
    return lead.drawUnits ?? unitsForDose(getDoseMcgAt(protocol, idx), lead.concentrationMcgPerUnit);
  }
  const p = getPhaseAt(protocol, idx);
  return p ? unitsForDose(p.doseMcg, lead.concentrationMcgPerUnit) : 0;
}
function getDisplayNameAt(protocol: Protocol, idx: number): string {
  return getGroupAt(protocol, idx)?.displayName ?? protocol.name;
}
// Legacy wrappers for protocol picker display - return undefined for empty protocols
function getDisplayComponent(protocol: Protocol) { return getComponentAt(protocol, 0); }
function getDisplayPhase(protocol: Protocol) { return getPhaseAt(protocol, 0); }
function getDisplayUnits(protocol: Protocol) { return getUnitsAt(protocol, 0); }

interface DoseLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-selected protocol (from dashboard schedule) */
  protocol: Protocol | null;
  /** All active protocols (for picker mode when no protocol pre-selected) */
  protocols?: Protocol[];
  /** Optional pre-filled date for backdating (e.g. logging a missed dose) */
  defaultDate?: Date;
  /**
   * ISO timestamp of the scheduled occurrence this log satisfies, when the
   * modal was opened from a specific slot (e.g. a missed dose). Stored as the
   * log's `scheduledTime` so the dose is reconciled against the day it was
   * DUE, not the day the user happened to enter it - otherwise backdating or
   * forward-dating leaves the missed row pending forever. Left undefined for
   * ad-hoc logs, which have no scheduled occurrence.
   */
  scheduledFor?: string;
  /** Fires after a successful save so parents can refetch their dose-log
   *  queries even if the global notify('dose_logs') pub/sub has missed
   *  a beat. Belt-and-suspenders against stale dashboard cards. */
  onSaved?: () => void;
}

export const DoseLogModal: React.FC<DoseLogModalProps> = ({ isOpen, onClose, protocol, protocols, defaultDate, scheduledFor, onSaved }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [selectedProtocol, setSelectedProtocol] = useState<Protocol | null>(protocol);
  const [selectedComponentIndex, setSelectedComponentIndex] = useState(0);
  const [selectedSite, setSelectedSite] = useState<InjectionSite>(InjectionSite.ABDOMEN_LEFT);
  const [lastSiteForPeptide, setLastSiteForPeptide] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingUnits, setEditingUnits] = useState(false);
  const [customUnits, setCustomUnits] = useState('');
  const [showFinalDosePrompt, setShowFinalDosePrompt] = useState(false);
  const [finalDoseInventoryId, setFinalDoseInventoryId] = useState<string | null>(null);
  // Set when the emptied vial's only backup is still sealed (never
  // reconstituted): the final-dose prompt then routes the user into
  // the inventory Start Using flow instead of a bare "mark empty".
  const [sealedBackup, setSealedBackup] = useState<InventoryItem | null>(null);
  // Set when an auto-promoted backup changed the draw - shows the "I
  // understand" modal (the summary refresh already fired in the data layer).
  const [promoteDrawChange, setPromoteDrawChange] = useState<{ oldUnits: number; newUnits: number; peptideName: string } | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [showComponentPicker, setShowComponentPicker] = useState(false);
  const [logDate, setLogDate] = useState('');
  const [logTime, setLogTime] = useState('');
  // Synchronous in-flight guard (P1-04 / C-05). React state is scheduled
  // and batched, so two taps within the same frame both see saving=false
  // and both fire handleLog. A ref flips synchronously on the first tap
  // and blocks the second before any await runs.
  const savingRef = useRef(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedProtocol(protocol);
      setSelectedComponentIndex(0);
      // Picker shows ADMINISTRATIONS: a combined group is one entry.
      setShowComponentPicker(protocol ? groupComponents(protocol).length > 1 : false);
      setNotes('');
      setLastSiteForPeptide(null);
      setShowFinalDosePrompt(false);
      setFinalDoseInventoryId(null);
      setSealedBackup(null);
      setEditingUnits(false);
      setCustomUnits('');
      // Use provided date (for missed doses) or default to now
      const d = defaultDate ?? new Date();
      // Use LOCAL date components, not d.toISOString() which returns UTC.
      // The UTC date can be tomorrow's date for users in negative-offset
      // timezones during late-evening logging - which silently dates the
      // log to tomorrow and breaks today-completion matching downstream.
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      setLogDate(`${y}-${m}-${day}`);
      setLogTime(d.toTimeString().slice(0, 5)); // HH:MM
    }
  }, [isOpen, protocol, defaultDate]);

  // Look up last injection site for the selected peptide
  useEffect(() => {
    if (!selectedProtocol || !isOpen) return;
    let cancelled = false;
    const comp = getComponentAt(selectedProtocol, selectedComponentIndex);
    if (!comp) return;
    fetchLastDoseForPeptide(comp.peptideId).then((lastLog) => {
      if (cancelled) return;
      if (lastLog) {
        const lastSite = lastLog.injectionSite as InjectionSite;
        setLastSiteForPeptide(INJECTION_SITE_LABELS[lastSite] || null);
        // Auto-select next rotation site
        const siteOrder = Object.values(InjectionSite);
        const lastIdx = siteOrder.indexOf(lastSite);
        if (lastIdx >= 0) {
          const nextSite = siteOrder[(lastIdx + 1) % siteOrder.length];
          setSelectedSite(nextSite);
        }
      } else {
        setLastSiteForPeptide(null);
      }
    });
    return () => { cancelled = true; };
  }, [selectedProtocol, selectedComponentIndex, isOpen]);

  // Effective units (custom or protocol default). Dose mcg stays from protocol.
  const effectiveUnits = customUnits ? parseFloat(customUnits) : (selectedProtocol ? getUnitsAt(selectedProtocol, selectedComponentIndex) : 0);
  // Off-protocol = entered units differ from the INSTRUCTED (0.5-snapped)
  // draw; a no-op open-and-close of the editor is not off-protocol. Semantics
  // + rationale live with the tested helpers in doseLogUnits.ts.
  const isOffProtocol = !!(
    selectedProtocol &&
    isOffProtocolUnits(customUnits, getUnitsAt(selectedProtocol, selectedComponentIndex))
  );
  // Blocks saving a 0 / negative / garbage draw, which would log a
  // meaningless 0 mcg dose that still decrements the vial.
  const unitsValid = isValidDrawUnits(effectiveUnits);

  const handleLog = async () => {
    if (!selectedProtocol) return;
    // Never persist a non-positive / NaN draw (doubly guarded: the Save
    // button is also disabled via unitsValid).
    if (!unitsValid) return;
    // Synchronous guard - must flip BEFORE any await. setState is async
    // and batched; a double-tap can fire both handlers before the first
    // setSaving(true) renders, so state alone is too slow.
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const group = getGroupAt(selectedProtocol, selectedComponentIndex);
      const component = getComponentAt(selectedProtocol, selectedComponentIndex);
      const phase = getPhaseAt(selectedProtocol, selectedComponentIndex);
      if (!group || !component || !phase) { setSaving(false); savingRef.current = false; return; }
      const loggedAt = new Date(`${logDate}T${logTime}:00`).toISOString();
      const now = new Date().toISOString();
      const standardUnitsForComp = getUnitsAt(selectedProtocol, selectedComponentIndex);
      // Show the RAW entered value for the drawn amount (formatUnits snaps
      // to 0.5, which could render "12.5 instead of 12.5" for a hand-typed
      // 12.4); the protocol side stays snapped since that's the instructed draw.
      const offProtocolNote = isOffProtocol
        ? `[Off-protocol: ${effectiveUnits} units instead of ${formatUnits(standardUnitsForComp)} units]`
        : '';
      const combinedNotes = [offProtocolNote, notes].filter(Boolean).join(' ') || undefined;

      // Find the vial this dose draws from. The shared resolver owns
      // the policy: linked id first, archived-link fallback to the
      // newest usable single (self-healing after mark-empty, P0-3),
      // sealed and mix rows never matched, combined groups never fall
      // back. We resolve BEFORE the atomic write so the RPC receives
      // the inventory id directly; the RPC itself locks the row and
      // re-validates ownership + active state.
      const linkedInventory = await resolveVialForComponent(
        { inventoryId: component.inventoryId, peptideId: component.peptideId },
        { isCombined: group.isCombined },
      );

      // Doses consumed: e.g., protocol of 10 units + drawn 20 units = 2 doses.
      let dosesConsumed = 0;
      if (linkedInventory && linkedInventory.isActive) {
        dosesConsumed = standardUnitsForComp > 0
          ? Math.max(1, Math.round(effectiveUnits / standardUnitsForComp))
          : 1;
      }

      // Atomic: dose_log insert(s) + inventory decrement in a single
      // Postgres transaction. Either all land or none do. See P1-03 /
      // C-04; combined groups use the v3 group RPC (one decrement for
      // the whole administration).
      const inventoryIdForRpc = linkedInventory && linkedInventory.isActive
        ? linkedInventory.id
        : null;
      let result: { inventoryDosesRemaining: number | null };

      if (group.isCombined) {
        // One physical draw = N per-component logs sharing one
        // administrationId. Off-protocol draws scale each component's
        // true mcg by the same ratio (the recipe ratio is fixed).
        const scale = standardUnitsForComp > 0 ? effectiveUnits / standardUnitsForComp : 1;
        const breakdown = group.components.map((c) => {
          const ph = getCurrentPhase(c);
          return {
            componentId: c.id,
            phaseId: ph.id,
            peptideId: c.peptideId,
            peptideName: c.peptideName,
            doseMcg: ph.doseMcg * scale,
          };
        });
        const totalMcg = breakdown.reduce((sum, b) => sum + b.doseMcg, 0);
        const groupDose: ScheduledDose = {
          id: `log-${group.mixGroupId}`,
          protocolId: selectedProtocol.id,
          componentId: component.id,
          phaseId: phase.id,
          peptideId: component.peptideId,
          peptideName: group.displayName,
          doseMcg: totalMcg,
          units: effectiveUnits,
          scheduledTime: '',
          isCompleted: false,
          mixGroupId: group.mixGroupId ?? undefined,
          groupComponents: breakdown,
        };
        const logs = buildAdministrationLogs(groupDose, {
          loggedTime: loggedAt,
          scheduledTime: scheduledFor,
          injectionSite: selectedSite,
          skipped: false,
          notes: combinedNotes,
        });
        result = await logGroupDoseWithInventory(logs, inventoryIdForRpc, dosesConsumed);
      } else {
        // Compute actual mcg from units drawn (for accurate vial tracking)
        const actualMcg = component.concentrationMcgPerUnit * effectiveUnits;
        const doseLog: DoseLog = {
          id: uuid(),
          protocolId: selectedProtocol.id,
          componentId: component.id,
          phaseId: phase.id,
          peptideId: component.peptideId,
          peptideName: component.peptideName,
          doseMcg: isOffProtocol ? actualMcg : phase.doseMcg,
          units: effectiveUnits,
          injectionSite: selectedSite,
          scheduledTime: scheduledFor,
          loggedTime: loggedAt,
          skipped: false,
          notes: combinedNotes,
          administrationId: uuid(),
          createdAt: now,
        };
        result = await logDoseWithInventory(doseLog, inventoryIdForRpc, dosesConsumed);
      }

      // Use the RPC's canonical post-decrement value to decide if the
      // vial is empty. Reading linkedInventory.dosesRemaining
      // pre-RPC raced with concurrent writes from another device —
      // both devices could see the same pre-decrement value, both
      // archive (or both miss the prompt) for what was actually the
      // last dose. The RPC holds doses_remaining inside its
      // transaction; that value is authoritative.
      if (
        inventoryIdForRpc &&
        result.inventoryDosesRemaining !== null &&
        result.inventoryDosesRemaining <= 0
      ) {
        // Vial just emptied. Try to auto-promote a single unstarted
        // backup of the same peptide. If exactly one backup exists,
        // skip the "is this empty?" prompt entirely - user already set
        // up the next vial when they hit Refill.
        const switchOutcome = await maybeAutoSwitchEmptyVial(inventoryIdForRpc);
        onSaved?.();
        if (switchOutcome.kind === 'promoted') {
          setSaving(false);
          savingRef.current = false;
          const change = switchOutcome.drawChange;
          if (change) {
            // The backup was mixed differently, so the instructed draw
            // changed. The summary refresh already fired inside
            // maybeAutoSwitchEmptyVial; here we make the user acknowledge
            // the new draw so they don't keep drawing the old amount from
            // muscle memory. Dismissing the modal closes this one.
            setPromoteDrawChange({
              oldUnits: change.oldUnits,
              newUnits: change.newUnits,
              peptideName: switchOutcome.newVial.peptideName,
            });
          } else {
            showToast(`Switched to your backup vial of ${switchOutcome.newVial.peptideName}`, 'success');
            onClose();
          }
          return;
        }
        if (switchOutcome.kind === 'sealed_backup') {
          // Backup exists but was never reconstituted - route the user
          // into the Start Using flow instead of silently promoting a
          // vial with no BAC water (P1-4).
          setSealedBackup(switchOutcome.backup);
        }
        // 0 backups, sealed backup, or 2+ backups -> the "is this vial
        // empty?" prompt (with Start Using routing when sealed).
        setFinalDoseInventoryId(inventoryIdForRpc);
        setShowFinalDosePrompt(true);
        setSaving(false);
        savingRef.current = false;
        return;
      }

      showToast(`Dose logged - ${getDisplayNameAt(selectedProtocol, selectedComponentIndex)}`, 'success');
      hapticNotification('success');
      setNotes('');
      setSelectedProtocol(null);
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Failed to log dose:', err);
      showToast("Couldn't save that dose. Try again in a sec.");
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const handleArchiveVial = async () => {
    if (!finalDoseInventoryId) return;
    setArchiving(true);
    try {
      // Archive AND clear component.inventoryId on any protocol still
      // pointing at this vial (P0-3: the old path archived only, so
      // every later dose resolved the dead vial and never decremented
      // anything again). Two writes, non-atomic by decision 8C - on
      // failure we toast and keep the prompt open so the user retries.
      await archiveVialAndUnlink(finalDoseInventoryId);
    } catch (err) {
      console.error('[dose-log] archive vial failed:', err);
      showToast("Couldn't mark that vial empty. Check your connection and try again.");
      setArchiving(false);
      return;
    }
    const goToBackup = sealedBackup;
    setArchiving(false);
    setShowFinalDosePrompt(false);
    setNotes('');
    setSelectedProtocol(null);
    onClose();
    if (goToBackup) {
      // Hand off to the My Supply tab's Start Using flow for the
      // sealed backup (it collects BAC water + starts the 28-day clock).
      navigate(`/protocols?tab=supply&startUsing=${goToBackup.id}#vial-${goToBackup.id}`);
    }
  };

  const handleKeepVial = () => {
    setShowFinalDosePrompt(false);
    setNotes('');
    setSelectedProtocol(null);
    onClose();
  };

  const sites = Object.values(InjectionSite);

  // Picker mode: show protocol list first
  const showPicker = !selectedProtocol && protocols && protocols.length > 0;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={showFinalDosePrompt ? 'Is this vial empty?' : showPicker ? 'Choose Protocol' : 'Log Dose'}
      toolbar
      onDone={!showFinalDosePrompt && !showPicker && selectedProtocol ? handleLog : undefined}
      doneLabel="Save"
      doneDisabled={!selectedProtocol || saving || (!showFinalDosePrompt && !showPicker && !unitsValid)}
      doneLoading={saving}
    >
      {showFinalDosePrompt ? (
        <div className="log-final-dose">
          <div className="log-final-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <p className="log-final-text">
            {sealedBackup
              ? 'Dose logged. That looked like the last dose in this vial, and your backup vial is not mixed yet. Mark this one empty and set up your backup?'
              : 'Dose logged. That looked like the last dose in this vial. Want to mark it empty?'}
          </p>
          <Button variant="primary" size="lg" fullWidth loading={archiving} onClick={handleArchiveVial}>
            {sealedBackup ? 'Mark empty and set up backup' : 'Yes, mark it empty'}
          </Button>
          <Button variant="ghost" size="md" fullWidth onClick={handleKeepVial}>
            No, keep it active
          </Button>
        </div>
      ) : showPicker ? (
        <div className="log-picker">
          {protocols!.map((p) => (
            <button
              key={p.id}
              className="log-picker-item"
              onClick={() => {
                setSelectedProtocol(p);
                if (groupComponents(p).length > 1) setShowComponentPicker(true);
              }}
            >
              <div className="log-picker-info">
                <span className="log-picker-name">{getDisplayNameAt(p, 0)}</span>
                <span className="log-picker-dose">{formatNumber(getDoseMcgAt(p, 0))} mcg · {formatUnits(getDisplayUnits(p))} units</span>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          ))}
        </div>
      ) : showComponentPicker && selectedProtocol ? (
        <div className="log-picker">
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-3)' }}>
            Which peptide are you logging a dose for?
          </p>
          {groupComponents(selectedProtocol).map((group, idx) => {
            const units = getUnitsAt(selectedProtocol, idx);
            return (
              <button
                key={group.mixGroupId ?? group.components[0].id}
                className="log-picker-item"
                onClick={() => {
                  setSelectedComponentIndex(idx);
                  setShowComponentPicker(false);
                }}
              >
                <div className="log-picker-info">
                  <span className="log-picker-name">{group.displayName}</span>
                  <span className="log-picker-dose">{formatUnits(units)} units</span>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            );
          })}
        </div>
      ) : selectedProtocol ? (
        <>
          <div className="log-summary">
            <span className="log-peptide">{getDisplayNameAt(selectedProtocol, selectedComponentIndex)}</span>
            <div className="log-dose-row">
              <span className="log-dose">{formatNumber(getDoseMcgAt(selectedProtocol, selectedComponentIndex))} mcg</span>
              <span className="log-dose-sep">·</span>
              {editingUnits ? (
                <div className="log-units-edit">
                  <input
                    type="number"
                    inputMode="decimal"
                    className="log-units-input"
                    value={customUnits}
                    onChange={(e) => setCustomUnits(e.target.value)}
                    autoFocus
                    onBlur={() => { if (customUnits) setEditingUnits(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') setEditingUnits(false); }}
                  />
                  <span className="log-units-suffix">units</span>
                </div>
              ) : (
                <button className="log-units-display" onClick={() => {
                  setCustomUnits(String(formatUnits(effectiveUnits)));
                  setEditingUnits(true);
                }}>
                  <span>{formatUnits(effectiveUnits)} units</span>
                  <span className="log-units-edit-label">edit</span>
                </button>
              )}
            </div>
            {isOffProtocol && (
              <span className="log-off-protocol-hint">
                Different from your usual - your protocol is {formatUnits(getUnitsAt(selectedProtocol, selectedComponentIndex))} units
              </span>
            )}
          </div>

          <div className="input-group log-datetime">
            <label className="input-label" htmlFor="log-date">Date & Time</label>
            <div className="log-datetime-row">
              <input
                id="log-date"
                type="date"
                className="log-datetime-input"
                value={logDate}
                onChange={(e) => setLogDate(e.target.value)}
              />
              <input
                type="time"
                className="log-datetime-input"
                value={logTime}
                onChange={(e) => setLogTime(e.target.value)}
              />
            </div>
          </div>

          <div className="input-group log-sites">
            <label className="input-label" htmlFor="log-sites-grid">
              Injection Site
              {lastSiteForPeptide && (
                <span className="log-last-site">Last: {lastSiteForPeptide} · Next suggested is pre-selected</span>
              )}
            </label>
            <div id="log-sites-grid" className="log-sites-grid">
              {sites.map((site) => (
                <button
                  key={site}
                  className={`log-site-btn ${selectedSite === site ? 'log-site-active' : ''}`}
                  onClick={() => setSelectedSite(site)}
                >
                  {INJECTION_SITE_LABELS[site]}
                </button>
              ))}
            </div>
          </div>

          <Input
            id="log-notes"
            label="Notes (optional)"
            placeholder="How did it feel? Anything you want to remember?"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {protocols && protocols.length > 1 && (
            <button className="log-change-protocol" onClick={() => setSelectedProtocol(null)}>
              Change peptide
            </button>
          )}

          <DosingDisclaimer variant="inline" />
        </>
      ) : (
        // Safety net: the modal opened with no protocol pre-selected AND no
        // list to pick from (e.g. a caller passed a null protocol while its
        // data was still loading, or activeProtocols came back empty). Every
        // other branch renders content, so without this the sheet body would
        // be empty and collapse to just the "Log Dose" toolbar stuck at the
        // bottom of the screen. Render a clear empty state instead so the
        // user is never staring at a blank sheet. (Bug: 2026-07-07.)
        <div className="log-empty" style={{ textAlign: 'center', padding: 'var(--space-2) 0 var(--space-4)' }}>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}>
            Pick a dose from your dashboard, or start a protocol, to log it here.
          </p>
          <Button variant="primary" size="lg" fullWidth onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </Modal>
    {promoteDrawChange && (
      <DrawChangeModal
        isOpen
        oldUnits={promoteDrawChange.oldUnits}
        newUnits={promoteDrawChange.newUnits}
        peptideName={promoteDrawChange.peptideName}
        onConfirm={() => { setPromoteDrawChange(null); onClose(); }}
      />
    )}
    </>
  );
};
